import React, { useState, useEffect } from 'react';
import {
  DocPage, PageHeader, Section, SubSection, Prerequisites,
  Para, Callout, DocTable,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './PricingDimensions.css';

const TOC = [
  { id: 'overview',      label: 'Overview' },
  { id: 'single-dim',    label: 'Single pricing dimension' },
  { id: 'multi-dim',     label: 'Multiple pricing dimensions' },
  { id: 'tag-groups',    label: 'Dimension groups' },
  { id: 'partial-tags',  label: 'Per-fee selection' },
  { id: 'on-invoices',   label: 'How pricing dimensions appear on invoices' },
];

// ── Tag group diagram ─────────────────────────────────────────────────────────

const DEFAULT_GROUPS = [
  {
    id: 'A',
    name: 'Zone Group 1',
    rateCard: 'Rate card A',
    values: [
      { label: 'Zone 1', defaultUsage: 120 },
      { label: 'Zone 2', defaultUsage: 80 },
    ],
  },
  {
    id: 'B',
    name: 'Zone Group 2',
    rateCard: 'Rate card B',
    values: [
      { label: 'Zone 3', defaultUsage: 60 },
    ],
  },
];

function TagGroupDiagram({ groups = DEFAULT_GROUPS, dimension = 'Zone' }) {
  const [usages, setUsages] = useState(() => {
    const init = {};
    groups.forEach((g) => g.values.forEach((v) => { init[v.label] = v.defaultUsage; }));
    return init;
  });

  const setUsage = (label, val) =>
    setUsages((prev) => ({ ...prev, [label]: Math.max(0, Number(val) || 0) }));

  const totalValues = groups.reduce((n, g) => n + g.values.length, 0);
  const totalGroups = groups.length;

  return (
    <div className="pt-tg-wrap">
      <div className="pt-tg-legend">
        <span className="pt-tg-legend-item">
          <span className="pt-tg-legend-num">{totalValues}</span> values
        </span>
        <span className="pt-tg-legend-arrow">→</span>
        <span className="pt-tg-legend-item">
          <span className="pt-tg-legend-num">{totalGroups}</span> groups
        </span>
        <span className="pt-tg-legend-arrow">→</span>
        <span className="pt-tg-legend-item">
          <span className="pt-tg-legend-num">{totalGroups}</span> rate cards
        </span>
        <span className="pt-tg-legend-note">(instead of {totalValues})</span>
      </div>

      <div className="pt-tg-table-wrap">
        <table className="pt-tg-table">
          <thead>
            <tr>
              <th className="pt-tg-th">{dimension} value</th>
              <th className="pt-tg-th pt-tg-th--usage">Usage this period</th>
              <th className="pt-tg-th pt-tg-th--group">Group</th>
              <th className="pt-tg-th pt-tg-th--billed">Rate card applied to</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group, gi) => {
              const total = group.values.reduce((sum, v) => sum + (usages[v.label] ?? 0), 0);
              const isLast = gi === groups.length - 1;
              return group.values.map((v, vi) => (
                <tr
                  key={v.label}
                  className={`pt-tg-tr${vi === group.values.length - 1 && !isLast ? ' pt-tg-tr--group-end' : ''}`}
                >
                  <td className="pt-tg-td pt-tg-td--value">
                    <span className="pt-tg-value-chip">{v.label}</span>
                  </td>
                  <td className="pt-tg-td pt-tg-td--usage">
                    <input
                      className="pt-tg-input"
                      type="number"
                      min="0"
                      value={usages[v.label]}
                      onChange={(e) => setUsage(v.label, e.target.value)}
                    />
                  </td>
                  {vi === 0 && (
                    <>
                      <td
                        rowSpan={group.values.length}
                        className={`pt-tg-td pt-tg-td--group pt-tg-td--group-${gi % 2 === 0 ? 'a' : 'b'}`}
                      >
                        <span className="pt-tg-group-name">{group.name}</span>
                        {group.values.length > 1 && (
                          <span className="pt-tg-group-hint">
                            {group.values.map((vv) => usages[vv.label] ?? 0).join(' + ')} = {total}
                          </span>
                        )}
                      </td>
                      <td
                        rowSpan={group.values.length}
                        className="pt-tg-td pt-tg-td--billed"
                      >
                        <span className="pt-tg-rc-label">{group.rateCard}</span>
                        <span className="pt-tg-rc-input">cost({total})</span>
                      </td>
                    </>
                  )}
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
      <p className="pt-tg-caption">
        Edit the usage numbers above — the aggregated totals update in real time.
      </p>
    </div>
  );
}

const CHIP_ACCENTS = ['purple', 'teal'];

function cartesian(arrays) {
  return arrays.reduce(
    (acc, arr) => acc.flatMap((prev) => arr.map((val) => [...prev, val])),
    [[]]
  );
}

// ── Tag combination builder ───────────────────────────────────────────────────

function TagBuilder({ dims }) {
  const [enabled, setEnabled] = useState(() => {
    const init = {};
    dims.forEach((d, i) => { init[d.key] = i === 0; });
    return init;
  });

  useEffect(() => {
    const init = {};
    dims.forEach((d, i) => { init[d.key] = i === 0; });
    setEnabled(init);
  }, [dims]);

  const activeDims = dims.filter((d) => enabled[d.key]);
  const combos = activeDims.length > 0 ? cartesian(activeDims.map((d) => d.values)) : [];

  const countLabel =
    combos.length === 0
      ? null
      : activeDims.length === 1
      ? `${combos.length} variation${combos.length !== 1 ? 's' : ''}`
      : `${combos.length} combinations (${activeDims.map((d) => d.values.length).join(' × ')})`;

  return (
    <div className="fct-builder">
      <div className="fct-builder-controls">
        <div className="fct-dims">
          {dims.map((dim) => (
            <button
              key={dim.key}
              className={`fct-dim${enabled[dim.key] ? ' fct-dim--on' : ''}`}
              onClick={() => setEnabled((prev) => ({ ...prev, [dim.key]: !prev[dim.key] }))}
            >
              <span className="fct-dim-dot" />
              <span className="fct-dim-name">{dim.label}</span>
              <span className="fct-dim-vals">{dim.values.join(', ')}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="fct-builder-result">
        {combos.length === 0 ? (
          <p className="fct-builder-empty">Enable a pricing dimension above to see combinations.</p>
        ) : (
          <>
            <span className="fct-builder-count">{countLabel}</span>
            <div className="fct-combos">
              {combos.map((combo, i) => (
                <div key={i} className="fct-combo">
                  {combo.map((val, j) => (
                    <span key={j} className={`fct-chip fct-chip--${CHIP_ACCENTS[j % CHIP_ACCENTS.length]}`}>
                      <span className="fct-chip-key">{activeDims[j].label}</span>
                      <span className="fct-chip-val">{val}</span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Pricing Dimension matrix: which fee components use which product-level attrs ──

function TagMatrix({ productDims, components }) {
  return (
    <div className="pt-matrix-wrap">
      <table className="pt-matrix">
        <thead>
          <tr>
            <th className="pt-matrix-th pt-matrix-th--comp">Fee Component</th>
            {productDims.map((d) => (
              <th key={d.key} className="pt-matrix-th">{d.label}</th>
            ))}
            <th className="pt-matrix-th pt-matrix-th--count">Combinations</th>
          </tr>
        </thead>
        <tbody>
          {components.map((comp) => {
            const usedDims = productDims.filter((d) => comp.uses.includes(d.key));
            const count = usedDims.length === 0
              ? 1
              : usedDims.reduce((acc, d) => acc * d.values.length, 1);
            const formula = usedDims.map((d) => d.values.length).join(' × ');

            return (
              <tr key={comp.name}>
                <td className="pt-matrix-td pt-matrix-td--comp">{comp.name}</td>
                {productDims.map((d) => (
                  <td key={d.key} className="pt-matrix-td pt-matrix-td--check">
                    {comp.uses.includes(d.key)
                      ? <span className="pt-matrix-yes">✓</span>
                      : <span className="pt-matrix-no">—</span>
                    }
                  </td>
                ))}
                <td className="pt-matrix-td pt-matrix-td--count">
                  {usedDims.length > 1 && <span className="pt-matrix-formula">{formula} = </span>}
                  <strong>{count}</strong>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PricingDimensions({ onNavigate }) {
  const { industry } = useIndustry();
  const tags = industry.rateAttributes;
  const dimNames = tags.partialTags.productDims.map(d => d.label).join(', ');
  const firstDim  = tags.partialTags.productDims[0];
  const firstVals = firstDim.values;
  const description = `A Pricing Dimension is an axis along which a fee's price varies — defined at the product level, opted into per fee. Each unique combination of dimension values gets its own rate card.`;

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Pricing"
        title="Pricing Dimensions"
        description={description}
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'product', label: 'Products' },
        ]}
      />

      <Section id="overview" title="Overview">
        <Para>
          A product defines the full set of pricing dimensions available for pricing. Each fee component
          within that product then declares which of those attributes it uses. A fee component only
          needs to use the attributes that actually affect its price — it does not have to use
          all of them.
        </Para>
        <Para>
          Common patterns in {industry.label.toLowerCase()} billing:
        </Para>
        <DocTable
          headers={['Use case', 'Pricing Dimension(s)', 'Example values']}
          rows={tags.useCases}
        />
      </Section>

      <Section id="single-dim" title="Single pricing dimension">
        <Para>{tags.singleDim.intro}</Para>
        <DocTable
          headers={tags.singleDim.headers}
          rows={tags.singleDim.rows}
        />
        <Para>{tags.singleDim.outro}</Para>
      </Section>

      <Section id="multi-dim" title="Multiple pricing dimensions">
        <Para>
          When pricing varies across more than one axis, define multiple pricing dimensions on the
          product. The system generates <strong>all combinations</strong> of their values for each
          fee component that uses those attributes.
        </Para>

        <SubSection id="multi-dim-example" title={`Example: ${tags.multiDim.headers.join(' × ')}`}>
          <Para>{tags.multiDim.intro}</Para>
          <DocTable
            headers={tags.multiDim.headers}
            rows={tags.multiDim.rows}
          />
          <Para>
            {tags.multiDim.dims.map((d) => d.values.length).join(' × ')} ={' '}
            {tags.multiDim.dims.reduce((acc, d) => acc * d.values.length, 1)} combinations
            for any fee component that uses both attributes. Toggle below to see how the count grows:
          </Para>
          <TagBuilder dims={tags.multiDim.dims} />
        </SubSection>
      </Section>

      <Section id="tag-groups" title="Dimension groups">
        <Para>
          By default, every pricing dimension value gets its own rate card slot. A product with a Zone
          dimension of three values — Zone 1, Zone 2, Zone 3 — produces three rate card slots.
          <strong> Dimension groups</strong> let you collapse multiple values into a single slot:
          Zone 1 and Zone 2 can be grouped together, so they share one rate card. Zone 3 stays in
          its own group with a separate rate card.
        </Para>
        <Para>
          The billing engine aggregates usage across all values in a group before applying the
          pricing model. If a customer ships 120 units via Zone 1 and 80 via Zone 2, the rate
          card for Zone Group 1 receives a single input of 200 — not two separate inputs of
          120 and 80. This matters when the pricing model has tiers: <strong>cost(200)</strong> is
          not the same as <strong>cost(120) + cost(80)</strong>.
        </Para>
        <TagGroupDiagram />
        <Callout type="info" title="Rate cards follow groups, not values">
          Once dimension groups are defined, you configure rate cards at the group level.
          Individual values no longer have their own rate card slots — they inherit the rate
          of their group. Changing a value's group reassigns it to a different rate card.
        </Callout>
        <Callout type="tip" title="When to use dimension groups">
          Use groups when multiple values should be priced identically and their usage should be
          pooled before tiered pricing runs. If each value truly needs independent pricing,
          leave them ungrouped — each gets its own rate card slot as usual.
        </Callout>
      </Section>

      <Section id="partial-tags" title="Per-component pricing dimension selection">
        <Para>
          A product can define several pricing dimensions without requiring every fee component to use
          all of them. Each fee component declares the subset of attributes relevant to its pricing.
          Components that don't use an attribute are priced without it — they have fewer combinations
          and simpler rate tables.
        </Para>
        <Callout type="info" title="Example">
          A {industry.label.toLowerCase()} product defines{' '}
          {tags.partialTags.productDims.map((d) => d.label).join(', ')} as pricing dimensions.
          Not every fee component needs all three.
        </Callout>
        <TagMatrix
          productDims={tags.partialTags.productDims}
          components={tags.partialTags.components}
        />
        <Callout type="tip">
          Usage events always carry all of the product's pricing dimension values. The billing engine
          uses only the subset each fee component has declared — the rest are ignored for that component.
        </Callout>
      </Section>

      <Section id="on-invoices" title="How pricing dimensions appear on invoices">
        <Para>{tags.invoiceBehavior}</Para>
        <Callout type="info" title="Only active combinations appear">
          If a customer has no usage for a particular pricing dimension combination in a billing period,
          that combination is omitted from their invoice. You define all valid combinations at the
          product level; billing only charges for what was consumed.
        </Callout>
      </Section>
    </DocPage>
  );
}
